import React from 'react';

import './picker.css';
import SaveButton from '../../components/saveButton';
import Copy from '../../components/copy';
import Modal from '../../components/modal';
import { systemText } from '../../helpers/string';
import Help from '../../components/help';

export const Picker = ({
	data,
	itemPrefix,
	title,
	value,
	error,
	help,
	label,
	entity,
	nameProperty,
	show,
	onSave,
	onClose,
	mandatory,
	mandatoryMessage,
	disabledMessage,
	saveLabel,
	filterOption,
	extra,
	footerExtra,
	itemHelp,
	filterOutDisabled,
	activeOnly,
}) => {
	const filterPropertyOptions = {
		staff: [
			{ name: 'Name', property: 'full_Name', icon: 'user' },
			{ name: 'Manager', property: 'manager_Full_Name', icon: 'users' },
			{ name: 'JobTitle', property: 'job_Title', array: 'role_Names', icon: 'address-card' },
			{ name: 'Department', property: 'department', array: 'department_Names', icon: 'building' },
			{ name: 'Location', property: 'location', array: 'location_Names', icon: 'map-marker' },
			{ name: 'BusinessUnit', property: 'business_Unit', array: 'business_Unit_Names', icon: 'cubes' },
		],
	};

	entity = entity || '';

	const filterProperties = filterOption ? filterPropertyOptions[filterOption] : null;

	const pageSize = 25;
	const [max, setMax] = React.useState(pageSize);
	const [saving, setSaving] = React.useState(false);
	const [filterOn, setFilterOn] = React.useState(filterProperties ? filterProperties[0].property : null);
	const [search, setSearch] = React.useState('');
	const [available, setAvailable] = React.useState([]);
	const [selected, setSelected] = React.useState([]);

	React.useEffect(() => {
		if (data) {
			setAvailable(data.filter(d => !value || !value.some(id => id === d.id)));
			setSelected(data.filter(d => value?.some(id => id === d.id)));
		}

		setSearch('');
	}, [data, value, show]);

	const addAll = rows => {
		setAvailable(available.filter(a => !rows.some(r => r.id === a.id)));
		setSelected(selected.concat(rows.filter(r => !selected.some(a => a.id === r.id))));
	};

	const removeAll = rows => {
		setAvailable(available.concat(rows.filter(r => !available.some(a => a.id === r.id))));
		setSelected(selected.filter(a => !rows.some(r => r.id === a.id)));
	};

	const add = id => {
		const item = available.find(d => d.id === id);
		setAvailable(available.filter(d => d.id !== id));
		setSelected(
			selected
				.filter(d => d.id !== id)
				.concat([item])
				.sort((a, b) => (a[nameProperty || 'name'] > b[nameProperty || 'name'] ? 1 : -1)),
		);
	};

	const remove = id => {
		const item = selected.find(d => d.id === id);
		setSelected(selected.filter(d => d.id !== id));
		setAvailable(
			available
				.filter(d => d.id !== id)
				.concat([item])
				.sort((a, b) => (a[nameProperty || 'name'] > b[nameProperty || 'name'] ? 1 : -1)),
		);
	};

	const closeMe = () => {
		onClose();
	};

	const footer = (
		<div className="flex flex-center">
			<div className="flex-grow text-left">{footerExtra?.()}</div>
			<SaveButton
				saving={saving}
				setSaving={setSaving}
				className="btn btn-success"
				id="picker-button-save"
				onClick={() =>
					filterOutDisabled
						? onSave(selected.filter(x => !x.disabled).map(x => x.id))
						: onSave(selected.map(x => x.id))
				}
			>
				{saveLabel || systemText('Admin/General/Picker/Buttons/Save')}
			</SaveButton>
			<button id="cancel-button" className="btn" data-testid="picker-button-cancel" onClick={closeMe}>
				{systemText('Admin/General/Picker/Buttons/Cancel')}
			</button>
		</div>
	);

	const entityRow = (p, i, f, title, disabled) => {
		let help = itemHelp?.(p);
		return (
			<div
				key={`entity-${i}-${p.id}`}
				className={`flex flex-center mb5 ${disabled ? 'disabled' : 'pointer'}`}
				onClick={disabled ? null : () => f(p.id)}
			>
				{itemPrefix && typeof itemPrefix === 'function' && itemPrefix(p)}
				{itemPrefix && typeof itemPrefix !== 'function' && <i className={`fa fa-${itemPrefix} mr10`} />}
				<div className="flex-grow">
					<span title={disabled ? disabledMessage : title}>{p[nameProperty || 'name']}</span>
					{help && <Help id={`item-help-${p.id}`}>{help}</Help>}
				</div>
			</div>
		);
	};

	const showMore = (extra, key) => (
		<div key={`show-more-${key}`} className="flex mb5 show-more">
			<div className="image-holder mr10" />
			<div className="flex-grow">
				<div className="mb5">{systemText('Admin/General/Picker/More', extra)}</div>
				<button className="btn btn-xs" onClick={() => setMax(max + pageSize)}>
					{systemText('Admin/General/Picker/LoadMore')}
				</button>
			</div>
		</div>
	);

	const isNameMatch = (item, value, filterOn) => {
		const filter = filterPropertyOptions.staff.find(x => x.property === filterOn);
		const filterArray = filter?.array;
		let match = !value;

		if (filterArray) {
			match = match || item[filterArray]?.some(x => x?.toLowerCase().indexOf(value?.toLowerCase()) >= 0);
		}

		match = match || item[filterOn || nameProperty || 'name']?.toLowerCase().indexOf(value?.toLowerCase()) >= 0;

		return match;
	};

	const availableFiltered = available.filter(
		d => d[entity === 'Users' || activeOnly ? 'active' : 'id'] && isNameMatch(d, search, filterOn),
	);
	const selectedFiltered = selected.filter(d => isNameMatch(d, search, filterOn));

	return (
		<Modal show={show} title={title} close={closeMe} footer={footer}>
			<div className="row">
				<div className="col-md-12 mb10">
					<div>
						<div id="picker" className={`form-group ${error ? 'has-error' : ''}`}>
							{label && <label htmlFor="item-name">{label}</label>}
							{help && (
								<span className="form-control-help">
									<Copy value={help} />
								</span>
							)}
							{extra?.()}
							<div className="row">
								<div className="col-md-12">
									<div className="search flex">
										<input
											type="text"
											className="form-control flex-grow"
											value={search}
											data-testid="picker-filter"
											onChange={e => setSearch(e.target.value)}
											placeholder={
												filterProperties
													? systemText(
															`Admin/General/Picker/Filters/${
																filterProperties.find(f => f.property === filterOn)?.name
															}`,
													  )
													: systemText(
															'Admin/General/Picker/Filters/Default',
															entity?.toLowerCase(),
													  )
											}
										/>
										{filterProperties && (
											<div className="text-right flex flex-center">
												{filterProperties.map(f => (
													<button
														key={`filter-${f.property}`}
														title={systemText(`Admin/General/Picker/Filters/${f.name}`)}
														className={`btn btn-sm ml2 ${
															filterOn === f.property ? 'btn-primary' : ''
														}`}
														onClick={() => {
															setSearch('');
															setFilterOn(f.property);
														}}
													>
														<i className={`fa fa-${f.icon}`} />
													</button>
												))}
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-6">
									<div className="mb5">
										<label>{systemText('Admin/General/Picker/Available', entity)}</label>
									</div>
									<button
										data-testid="button-picker-add-all"
										className="btn btn-primary btn-xs mb10"
										disabled={availableFiltered.length === 0}
										onClick={() => addAll(availableFiltered)}
									>
										{systemText('Admin/General/Picker/AddAll')}
										<i className="fa fa-chevron-right ml10" />
									</button>
									<div id="available-list" className="mb10">
										{availableFiltered.length === 0 && (
											<span className="italic">
												{systemText('Admin/General/Picker/Available/None', entity?.toLowerCase())}
											</span>
										)}
										{availableFiltered.map((p, i) => {
											if (i < max)
												return entityRow(
													p,
													i,
													add,
													systemText('Admin/General/Picker/Available/Hover'),
													p.disabled,
												);
											if (i === max) return showMore(availableFiltered.length - max, 'available');
											return null;
										})}
									</div>
								</div>
								<div className="col-sm-6">
									<div className="mb5">
										<label>
											{systemText('Admin/General/Picker/Selected', entity)} ({selected.length})
										</label>
									</div>
									<button
										data-testid="button-picker-remove-all"
										className="btn btn-primary btn-xs mb10"
										disabled={selectedFiltered.filter(x => !x.disabled).length === 0}
										onClick={() => removeAll(selectedFiltered.filter(x => !x.disabled))}
									>
										<i className="fa fa-chevron-left mr10" />
										{systemText('Admin/General/Picker/RemoveAll')}
									</button>
									<div id="selected-list" className="mb10">
										{selectedFiltered.length === 0 && (
											<span className="italic">
												{systemText('Admin/General/Picker/Selected/None', entity?.toLowerCase())}
											</span>
										)}
										{selectedFiltered.map((p, i) => {
											if (i < max)
												return entityRow(
													p,
													i,
													remove,
													mandatory
														? systemText('Admin/General/Picker/Selected/Hover/Mandatory')
														: systemText('Admin/General/Picker/Selected/Hover'),
													mandatory || p.disabled,
												);
											if (i === max) return showMore(selectedFiltered.length - max, 'selected');
											return null;
										})}
										{mandatory && selectedFiltered.length > 0 && (
											<div className="italic text-danger mt20">
												{mandatoryMessage ||
													systemText('Admin/General/Picker/Mandatory', entity?.toLowerCase())}
											</div>
										)}
									</div>
								</div>
							</div>
							{error && <span className="help-block">{typeof error === 'function' ? error() : error}</span>}
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default Picker;
